import { CheckIcon, XIcon } from '@primer/octicons-react';
import React, { Suspense, useEffect } from 'react';
import { graphql, useFragment, useQueryLoader } from 'react-relay';

import {
  TaskFirstLabelingDialog_task$key,
  UnitSchoolGradeEnum,
  UnitSchoolYearEnum,
} from '../../../relay/__generated__/TaskFirstLabelingDialog_task.graphql';
import { TaskFirstLabelingDialog_taskFirstLabelingMutation } from '../../../relay/__generated__/TaskFirstLabelingDialog_taskFirstLabelingMutation.graphql';
import { TaskFirstLabelingDialog_unitDsQuery } from '../../../relay/__generated__/TaskFirstLabelingDialog_unitDsQuery.graphql';
import Button from '../../core/Button';
import Card from '../../core/Card';
import DescriptionList from '../../core/DescriptionList';
import Dialog, { DialogProps } from '../../core/Dialog';
import EmptyState from '../../core/EmptyState';
import EnumPair from '../../core/EnumPair';
import FormLayout from '../../core/FormLayout';
import Grid from '../../core/Grid';
import IconButton from '../../core/IconButton';
import Image from '../../core/Image';
import ItemList from '../../core/ItemList';
import Katex from '../../core/Katex';
import Label from '../../core/Label';
import MutationFormik, { MutationFormikProps } from '../../core/MutationFormik';
import NonFieldError from '../../core/NonFieldError';
import NumberField from '../../core/NumberField';
import PreloadedQueryRenderer from '../../core/PreloadedQueryRenderer';
import SelectField from '../../core/SelectField';
import Spinner from '../../core/Spinner';
import Stack from '../../core/Stack';
import TabHandler from '../../core/TabHandler';
import Text from '../../core/Text';
import View from '../../core/View';
import UnitDefaultRatingFetchQueryFormik from '../../unit/UnitDefaultRatingFetchQueryFormik';
import UnitDItem from '../../unitD/UnitDItem';
import UnitDPaginator from '../../unitD/UnitDPaginator';
import UnitDSelectDialogTokenField from '../../unitD/UnitDSelectDialogTokenField';

const TaskFirstLabelingDialog_task = graphql`
  fragment TaskFirstLabelingDialog_task on Task {
    id
    course {
      id
    }
    title
    sequence
    scrap {
      id
      problems {
        id
        objectUrl
      }
      solutions {
        id
        objectUrl
      }
    }
    unit {
      id
      schoolType
      schoolYears
      schoolGrades
    }
    problem {
      id
      problem
      solution
      problemType
      answer
      answerChoiceCount
      answerType
    }
    firstLabeling {
      id
      difficulty
      unitDs(order: { order: DESC }) {
        id
      }
    }
  }
`;

const unitDsForTaskFirstLabelingDialog = graphql`
  query TaskFirstLabelingDialog_unitDsQuery($filters: UnitDFilter, $order: UnitDOrder, $first: Int, $after: String) {
    ...UnitDPaginator_query @arguments(filters: $filters, order: $order, first: $first, after: $after)
  }
`;

type Props = { task: TaskFirstLabelingDialog_task$key } & DialogProps &
  Pick<MutationFormikProps<TaskFirstLabelingDialog_taskFirstLabelingMutation>, 'config' | 'onSubmit' | 'connections'>;

const TaskFirstLabelingDialog = ({ task: taskReference, config, onSubmit, connections, ...props }: Props) => {
  const task = useFragment(TaskFirstLabelingDialog_task, taskReference);
  const { id, course, sequence, title, problem, scrap, firstLabeling, unit } = task;

  const problemTabItems = [
    { id: 'all', label: '창작 + 스크랩' },
    { id: 'creation', label: '창작' },
    { id: 'scrap', label: '스크랩' },
  ];

  const [unitDsQueryReference, unitDsLoadQuery] = useQueryLoader<TaskFirstLabelingDialog_unitDsQuery>(
    unitDsForTaskFirstLabelingDialog,
  );

  useEffect(() => {
    if (firstLabeling && firstLabeling.unitDs.length > 0)
      unitDsLoadQuery({ filters: { id_In: firstLabeling.unitDs.map(({ id }) => id) }, order: { order: 'DESC' } });
  }, []);

  return (
    <Dialog {...props}>
      <MutationFormik<TaskFirstLabelingDialog_taskFirstLabelingMutation>
        mutation={graphql`
          mutation TaskFirstLabelingDialog_taskFirstLabelingMutation($input: TaskFirstLabelingInput!) {
            taskFirstLabeling(input: $input) {
              id
              difficulty
              unitDs(order: { order: DESC }) {
                id
              }
            }
          }
        `}
        initialValues={{
          task: id,
          unitDs: firstLabeling?.unitDs.map(({ id }) => id) || [],
          difficulty: firstLabeling?.difficulty || 0,
        }}
        config={{
          ...config,
          updater: (store, data) => {
            config?.updater?.(store, data);
            const task = store.get(id);
            const labeling = store.getRootField('taskFirstLabeling');
            task?.setLinkedRecord(labeling, 'firstLabeling');
          },
        }}
        onSubmit={onSubmit}
        connections={connections}
      >
        {({ values, setFieldValue, handleSubmit, dirty }, { scrollContainerRef, nonFieldErrorRef }) => (
          <>
            <Dialog.Header>
              <Grid sx={{ paddingRight: 4 }}>
                <Grid.Unit size={'max'}>
                  <Stack gapX={1}>
                    <Stack.Item>
                      <Text sx={{ fontSize: 3, fontWeight: 'bold' }}>1차 라벨링하기</Text>
                    </Stack.Item>
                    {problem ? (
                      <Stack.Item>
                        <Suspense>
                          <View>
                            <Label variant={'accent'}>
                              <EnumPair typename={'TaskProblemTypeEnum'}>{problem.problemType}</EnumPair>
                              {` · ${title}`}
                            </Label>
                          </View>
                        </Suspense>
                      </Stack.Item>
                    ) : null}
                    <Stack.Item>
                      <Label variant={'primary'}>{sequence}</Label>
                    </Stack.Item>
                  </Stack>
                </Grid.Unit>
                <Grid.Unit size={'min'}>
                  <Button variant={'primary'} onClick={() => handleSubmit()} leadingIcon={CheckIcon} disabled={!dirty}>
                    저장하기
                  </Button>
                </Grid.Unit>
              </Grid>
            </Dialog.Header>
            <Grid wrap={false} sx={{ overflowY: 'hidden', height: '100%' }}>
              <Grid.Unit size={3 / 4}>
                <TabHandler initialSelectIndex={0}>
                  {({ selectedIndex, handleSelect }) => (
                    <View
                      ref={scrollContainerRef}
                      sx={{
                        'padding': 3,
                        '&>*:not(:last-child)': { marginBottom: 5 },
                        'height': '100%',
                        'overflowY': 'auto',
                      }}
                    >
                      <NonFieldError ref={nonFieldErrorRef} />
                      <Stack gapX={1}>
                        <ItemList
                          items={problemTabItems}
                          renderItem={({ label }, index) => (
                            <Button
                              onClick={() => handleSelect(index)}
                              variant={selectedIndex === index ? 'outline' : 'invisible'}
                            >
                              {label}
                            </Button>
                          )}
                          renderItemWrapper={(children, { id }) => <Stack.Item key={id}>{children}</Stack.Item>}
                        />
                      </Stack>
                      <Grid gapX={2} gapY={3}>
                        {problemTabItems[selectedIndex].id === 'all' ||
                        problemTabItems[selectedIndex].id === 'creation' ? (
                          <>
                            <Grid.Unit size={1 / 2}>
                              <Text fontSize={1}>창작한 문제</Text>
                              <Card
                                sx={{
                                  height: problemTabItems[selectedIndex].id === 'all' ? '33vh' : '70vh',
                                  overflowY: 'auto',
                                  marginTop: 1,
                                }}
                              >
                                <View sx={{ padding: 2 }}>
                                  <Katex>{problem?.problem || ''}</Katex>
                                </View>
                              </Card>
                            </Grid.Unit>
                            <Grid.Unit size={1 / 2}>
                              <Text fontSize={1}>창작한 해설</Text>
                              <Card
                                sx={{
                                  height: problemTabItems[selectedIndex].id === 'all' ? '33vh' : '70vh',
                                  overflowY: 'auto',
                                  marginTop: 1,
                                }}
                              >
                                <View sx={{ padding: 2 }}>
                                  <Katex>{problem?.solution || ''}</Katex>
                                </View>
                              </Card>
                            </Grid.Unit>
                          </>
                        ) : null}
                        {problemTabItems[selectedIndex].id === 'all' ||
                        problemTabItems[selectedIndex].id === 'scrap' ? (
                          <>
                            <Grid.Unit size={1 / 2}>
                              <Text fontSize={1}>스크랩한 문제</Text>
                              <Card
                                sx={{
                                  height: problemTabItems[selectedIndex].id === 'all' ? '33vh' : '70vh',
                                  overflowY: 'auto',
                                  marginTop: 1,
                                }}
                              >
                                <ItemList
                                  items={scrap?.problems || []}
                                  renderItem={({ objectUrl }) => (
                                    <Image src={objectUrl} alt={objectUrl} html style={{ display: 'block' }} />
                                  )}
                                  renderItemWrapper={(children, { id }) => <View key={id}>{children}</View>}
                                  emptyState={
                                    <View
                                      sx={{
                                        display: 'flex',
                                        height: '100%',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <EmptyState title={'스크랩한 문제가 없습니다'} />
                                    </View>
                                  }
                                />
                              </Card>
                            </Grid.Unit>
                            <Grid.Unit size={1 / 2}>
                              <Text fontSize={1} sx={{ marginTop: 3 }}>
                                스크랩한 해설
                              </Text>
                              <Card
                                sx={{
                                  height: problemTabItems[selectedIndex].id === 'all' ? '33vh' : '70vh',
                                  overflowY: 'auto',
                                  marginTop: 1,
                                }}
                              >
                                <ItemList
                                  items={scrap?.solutions || []}
                                  renderItem={({ objectUrl }) => (
                                    <Image src={objectUrl} alt={objectUrl} html style={{ display: 'block' }} />
                                  )}
                                  renderItemWrapper={(children, { id }) => <View key={id}>{children}</View>}
                                  emptyState={
                                    <View
                                      sx={{
                                        display: 'flex',
                                        height: '100%',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                      }}
                                    >
                                      <EmptyState title={'스크랩한 해설이 없습니다'} />
                                    </View>
                                  }
                                />
                              </Card>
                            </Grid.Unit>
                          </>
                        ) : null}
                      </Grid>
                    </View>
                  )}
                </TabHandler>
              </Grid.Unit>
              <Grid.Unit size={1 / 4}>
                <View sx={{ padding: 3, backgroundColor: 'canvas.inset', height: '100%', overflowY: 'auto' }}>
                  <FormLayout>
                    <View>
                      <Text fontSize={2} fontWeight={'bold'}>
                        문제 정보
                      </Text>
                    </View>
                    <Card
                      sx={{
                        padding: 3,
                        backgroundColor: 'canvas.inset',
                      }}
                    >
                      <DescriptionList
                        item={task}
                        itemDescriptions={{
                          answerType: {
                            title: '문제 유형',
                            renderValue: ({ problem }) =>
                              problem?.answerType ? (
                                <Suspense>
                                  <EnumPair typename={'TaskProblemAnswerTypeEnum'}>{problem?.answerType}</EnumPair>
                                </Suspense>
                              ) : undefined,
                          },
                          answer: {
                            title: '정답',
                            renderValue: ({ problem }) => problem?.answer,
                          },
                          answerChoiceCount: {
                            title: '선지 개수',
                            renderValue: ({ problem }) =>
                              problem?.answerChoiceCount !== 0 ? problem?.answerChoiceCount : undefined,
                          },
                        }}
                        picks={['answerType', 'answer', 'answerChoiceCount']}
                        titleUnitSize={1 / 2}
                        descriptionUnitSize={1 / 2}
                      />
                    </Card>
                    <View>
                      <Text fontSize={2} fontWeight={'bold'}>
                        Rating
                      </Text>
                    </View>
                    <Suspense>
                      {unit ? (
                        <UnitDefaultRatingFetchQueryFormik
                          initialValues={{
                            schoolYear: '' as UnitSchoolYearEnum,
                            schoolGrade: '' as UnitSchoolGradeEnum,
                          }}
                          onCompleteFetch={({ unitDefaultRating }) => {
                            setFieldValue('difficulty', unitDefaultRating);
                          }}
                        >
                          {({ values, setFieldValue, handleSubmit }) => {
                            return (
                              <>
                                <Suspense
                                  fallback={
                                    <View padding={2}>
                                      <Spinner size={'small'} />
                                    </View>
                                  }
                                >
                                  <SelectField
                                    label={'학년'}
                                    placeholder={'학년 선택'}
                                    name={'schoolYear'}
                                    options={unit.schoolYears.map((schoolYear) => ({
                                      id: schoolYear,
                                      text: (
                                        <EnumPair typename={'UnitSchoolYearEnum'}>{schoolYear}</EnumPair>
                                      ) as unknown as string,
                                    }))}
                                    onChange={(value) => {
                                      setFieldValue('schoolYear', value);
                                      if (values.schoolGrade) handleSubmit();
                                    }}
                                  />
                                </Suspense>
                                <Suspense
                                  fallback={
                                    <View padding={2}>
                                      <Spinner size={'small'} />
                                    </View>
                                  }
                                >
                                  <SelectField
                                    label={'등급'}
                                    placeholder={'등급 선택'}
                                    name={'schoolGrade'}
                                    options={unit.schoolGrades.map((schoolGrade) => ({
                                      id: schoolGrade,
                                      text: (
                                        <EnumPair typename={'UnitSchoolGradeEnum'}>{schoolGrade}</EnumPair>
                                      ) as unknown as string,
                                    }))}
                                    onChange={(value) => {
                                      setFieldValue('schoolGrade', value);
                                      if (values.schoolYear) handleSubmit();
                                    }}
                                  />
                                </Suspense>
                              </>
                            );
                          }}
                        </UnitDefaultRatingFetchQueryFormik>
                      ) : null}
                    </Suspense>
                    <NumberField label={'Rating'} name={'difficulty'} required min={0} max={1000} />
                    <View>
                      <Text fontSize={2} fontWeight={'bold'}>
                        단원
                      </Text>
                    </View>
                    <Suspense
                      fallback={
                        <View padding={2}>
                          <Spinner size={'small'} />
                        </View>
                      }
                    >
                      <UnitDSelectDialogTokenField
                        label={'Unit D'}
                        name={'unitDs'}
                        placeholder={'Unit A > B > C > D 선택'}
                        caption={'최대 3개 선택 가능'}
                        variables={
                          unit
                            ? {
                                filters: {
                                  ...(course?.id
                                    ? {
                                        courseId_In: [course?.id],
                                      }
                                    : {}),

                                  unitSchoolType_Exact: unit.schoolType,
                                },
                              }
                            : undefined
                        }
                        onChange={(id) => {
                          const unitDs = values.unitDs.includes(id)
                            ? values.unitDs.filter((unitD) => unitD !== id)
                            : [...values.unitDs, id];
                          setFieldValue('unitDs', unitDs);
                          unitDsLoadQuery({ filters: { id_In: unitDs }, order: { order: 'DESC' } });
                        }}
                      />
                    </Suspense>
                    <View>
                      <Suspense
                        fallback={
                          <View padding={2}>
                            <Spinner size={'small'} />
                          </View>
                        }
                      >
                        {unitDsQueryReference ? (
                          <PreloadedQueryRenderer
                            query={unitDsForTaskFirstLabelingDialog}
                            queryReference={unitDsQueryReference}
                          >
                            {(queryReference) => (
                              <UnitDPaginator fragmentReference={queryReference}>
                                {({ unitDs }) => (
                                  <ItemList
                                    items={unitDs.edges}
                                    renderItem={({ node }) => <UnitDItem unitD={node} />}
                                    renderItemWrapper={(children, { node }, index) => (
                                      <View
                                        key={node.id}
                                        sx={{
                                          marginTop: index === 0 ? 0 : 2,
                                          borderRadius: 2,
                                          backgroundColor: 'neutral.muted',
                                          padding: 2,
                                        }}
                                      >
                                        <Grid wrap={false}>
                                          <Grid.Unit size={'max'}>
                                            {index === 0 ? (
                                              <Label sx={{ marginBottom: 1 }} variant={'default'}>
                                                Last Unit D
                                              </Label>
                                            ) : null}
                                            {children}
                                          </Grid.Unit>
                                          <Grid.Unit size={'min'}>
                                            <IconButton
                                              icon={XIcon}
                                              variant={'plain'}
                                              aria-label={'remove unitD'}
                                              onClick={() => {
                                                const unitDs = values.unitDs.filter((unitD) => unitD !== node.id);
                                                setFieldValue('unitDs', unitDs);
                                                unitDsLoadQuery({
                                                  filters: { id_In: unitDs },
                                                  order: { order: 'DESC' },
                                                });
                                              }}
                                            />
                                          </Grid.Unit>
                                        </Grid>
                                      </View>
                                    )}
                                  />
                                )}
                              </UnitDPaginator>
                            )}
                          </PreloadedQueryRenderer>
                        ) : null}
                      </Suspense>
                    </View>
                  </FormLayout>
                </View>
              </Grid.Unit>
            </Grid>
          </>
        )}
      </MutationFormik>
    </Dialog>
  );
};

export default TaskFirstLabelingDialog;
